import { Linking, useWindowDimensions } from 'react-native';
import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
} from 'react-native';
import Modal from 'react-native-modal';
import { Icon, Text } from 'react-native-elements';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import HTML from 'react-native-render-html';

import { getTopImageUrl } from '../../lib/news';
import { trackNewsDetailView } from '../../lib/analytics';
import Carousel, { DisplayType } from '../../components/Carousel';
import Divider from '../../components/Divider';
import { NewsItem, Image as NewsItemImage, Action } from '../../api/adserver';
import config from '../../lib/config';
import ZTImage from '../ZTImage';

export interface Props {
  item?: NewsItem | null;
  onClose: () => void;
}

interface NewsItemAction {
  id: string;
  title: string;
  icon: string | null;
  action: Action;
}

const NewsItemOverlay: React.FC<Props> = ({ item, onClose }) => {
  const [visible, setVisible] = useState(true);
  const windowDimensions = useWindowDimensions();

  useEffect(() => {
    if (item) {
      trackNewsDetailView(item);
    }
  }, [item]);

  const showClose = true;

  if (!item) {
    return null;
  }

  const handleOnClose = () => {
    setVisible(false);
    onClose && onClose();
  };

  let topImageUrl = getTopImageUrl(item);

  const hasGallery = item.gallery && item.gallery.length > 0;

  let newsItemCaroselImages: NewsItemImage[] = [];
  if (hasGallery && item.gallery) {
    newsItemCaroselImages = item.gallery;
  }

  return (
    <Modal
      backdropColor={'#000000'}
      backdropOpacity={0.5}
      isVisible={visible}
      hideModalContentWhileAnimating
      style={styles.overlay}
      onBackdropPress={onClose}
    >
      {showClose && (
        <View style={styles.topOverlay}>
          <View style={styles.closeButtonContainer}>
            <TouchableOpacity onPress={handleOnClose}>
              <Icon
                tvParallaxProperties={null}
                activeOpacity={0.5}
                name={'close'}
                size={30}
                color={'#505050'}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}
      <SafeAreaView>
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          style={styles.scrollView}
        >
          {topImageUrl && (
            <>
              <ZTImage uri={topImageUrl} style={styles.mainImage} />
              <Divider />
            </>
          )}
          <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
            <Text style={styles.featureHeading}>{item.title}</Text>
            <HTML
              source={{ html: item.body }}
              contentWidth={windowDimensions.width}
            />
          </View>
          <Divider />
          {item.attachments.length > 0 &&
            item.attachments.map((image) => (
              <View key={image.id}>
                <View style={styles.attachment}>
                  <View style={styles.fileInfo}>
                    <FontAwesome
                      name={'file-text-o'}
                      size={30}
                      color={config.theme.brandColor}
                      style={styles.iconAttachment}
                    />
                    <Text style={styles.fileName}>{image.name}</Text>
                  </View>
                  <Icon
                    tvParallaxProperties={null}
                    name={'ios-arrow-down-outline'}
                    type="ionicon"
                    size={30}
                    color={config.theme.brandColor}
                    onPress={() => {
                      Linking.openURL(image.url);
                    }}
                  />
                </View>
              </View>
            ))}
          <Divider />
          {hasGallery && (
            <>
              <Divider />
              <View
                style={[
                  styles.featureHeadingContainer,
                  styles.sectionStart,
                  styles.screenEnd,
                ]}
              >
                <Text style={[styles.featureHeading, styles.subSectionHeading]}>
                  Gallery
                </Text>
                <Carousel
                  name="newsItemcarousel"
                  displayType={DisplayType.photo}
                  items={newsItemCaroselImages}
                />
              </View>
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    margin: 0,
    marginTop: 40,
  },
  closeButtonContainer: {
    height: '100%',
    zIndex: 1000,
    flex: 1,
    alignContent: 'center',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginStart: 10,
  },
  topOverlay: {
    flex: 1,
    position: 'absolute',
    opacity: 0.5,
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    overflow: 'hidden',
    right: 0,
    top: 0,
    width: '100%',
    height: 40,
    fontWeight: 'bold',
    zIndex: 1001,
    backgroundColor: '#ffffff',
    padding: 0,
    marginTop: 0,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    borderRadius: 10,
    minHeight: '100%',
  },
  featureHeadingContainer: {
    marginBottom: 5,
    marginHorizontal: 15,
  },
  sectionStart: {
    marginTop: 20,
    marginBottom: 20,
  },
  featureHeading: {
    fontSize: config.theme.headingFontSize,
    fontWeight: config.theme.headingFontWeight,
    color: config.theme.headingTextColor,
    marginBottom: 10,
  },
  subSectionHeading: {
    fontSize: 18,
    color: config.theme.advert.subSectionHeadingTextColor,
    fontWeight: '700',
  },
  mainImage: {
    height: 204,
    width: '100%',
  },
  detailText: {
    fontSize: 14,
  },
  bottomMargin1Em: {
    marginBottom: 14,
  },
  screenEnd: {
    marginBottom: 20,
  },
  attachment: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  fileInfo: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fileName: {
    color: config.theme.brandColor,
    maxWidth: 300,
  },
  iconAttachment: {
    marginEnd: 15,
  },
});

export default NewsItemOverlay;
